import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, AlertCircle } from 'lucide-react';

const ValidationResults = ({ result, onCreateSubscription, loading }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLIANT':
        return <CheckCircle className="h-6 w-6 text-green-600" />;
      case 'NON_COMPLIANT':
        return <XCircle className="h-6 w-6 text-red-600" />;
      case 'NEEDS_REVIEW':
        return <AlertTriangle className="h-6 w-6 text-yellow-600" />;
      default:
        return <AlertCircle className="h-6 w-6 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLIANT':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'NON_COMPLIANT':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'NEEDS_REVIEW':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getSoxImpactColor = (impact) => {
    switch (impact) {
      case 'CRITICAL':
        return 'text-red-600 bg-red-50';
      case 'HIGH':
        return 'text-orange-600 bg-orange-50';
      case 'MEDIUM':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-blue-800 bg-blue-50';
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Validation Results
          </h2>
          <div className={`flex items-center space-x-2 px-4 py-2 rounded-lg border ${getStatusColor(result.compliance_status)}`}>
            {getStatusIcon(result.compliance_status)}
            <span className="font-medium">{result.compliance_status}</span>
          </div>
        </div>

        {result.errors && result.errors.length > 0 && (
          <div className="mb-6">
            <h3 className="text-md font-semibold text-gray-900 mb-3">
              SOX Compliance Issues
            </h3>
            <div className="space-y-3">
              {result.errors.map((error, index) => (
                <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <XCircle className="h-5 w-5 text-red-600 mr-2" />
                        <span className="font-medium text-red-900">
                          {error.field.replace(/_/g, ' ').toUpperCase()}
                        </span>
                        <span className={`ml-3 px-2 py-1 text-xs rounded-full ${getSoxImpactColor(error.sox_impact)}`}>
                          {error.sox_impact}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 mb-2">
                        <span className="font-medium">Expected:</span> {error.expected}
                      </p>
                      <p className="text-sm text-gray-700 mb-2">
                        <span className="font-medium">Actual:</span> {error.actual}
                      </p>
                      <p className="text-sm text-blue-800">
                        <span className="font-medium">Remediation:</span> {error.remediation}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {result.warnings && result.warnings.length > 0 && (
          <div className="mb-6">
            <h3 className="text-md font-semibold text-gray-900 mb-3">
              Warnings
            </h3>
            <div className="space-y-2">
              {result.warnings.map((warning, index) => (
                <div key={index} className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <AlertTriangle className="h-4 w-4 text-yellow-600 mr-2" />
                    <span className="text-sm text-yellow-800">{warning}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-6">
          <div>
            <h3 className="text-md font-semibold text-gray-900 mb-3">
              SOF Data
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Customer:</span>
                  <span className="text-sm font-medium">{result.sof_data.customer_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Deal ID:</span>
                  <span className="text-sm font-medium">{result.sof_data.deal_id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Amount:</span>
                  <span className="text-sm font-medium">
                    {result.sof_data.currency} {result.sof_data.total_amount}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Term:</span>
                  <span className="text-sm font-medium">{result.sof_data.term_length} months</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Discount:</span>
                  <span className="text-sm font-medium">{result.sof_data.discount_percent}%</span>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-md font-semibold text-gray-900 mb-3">
              Chargebee Data
            </h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Subscription ID:</span>
                  <span className="text-sm font-medium">{result.chargebee_data?.subscription_id || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Status:</span>
                  <span className="text-sm font-medium">{result.chargebee_data?.status || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Plan Amount:</span>
                  <span className="text-sm font-medium">
                    {result.chargebee_data?.currency_code} {(result.chargebee_data?.plan_amount / 100) || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {result.valid && (
          <div className="mt-6 flex justify-end">
            <button
              onClick={onCreateSubscription}
              disabled={loading}
              className="px-4 py-2 bg-blue-800 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Subscription in Chargebee'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ValidationResults;